import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {ConfigPageComponent} from '../../../../../core/shared/mvs-page/config-page.component';
import {WidgetConfigInterface} from '../../../../../core/shared/mvs-page/interface/widget-config-page.interface';
import {NavigationItem} from '../../../../../core/shared/dto/navigation/navigation-item';
import {WidgetData} from '../../../../../core/shared/widget/widget-data';
import {WidgetDataParam} from '../../../../../core/shared/widget/widget-data-param';
import {WidgetFactory} from '../../../../../core/shared/widget/service/widget.factory';
import {FilterCriteria} from '../../../../../core/shared/filter/api/filter.criteria';
import {Sorting} from '../../../../../core/shared/misc/sorting';
import {MvsCoreService} from '../../../../../core/shared/service/mvs-core.service';
import {ObjectIdentifierData} from '../../../../../core/shared/basic/object-identifier-data';
import {ObjectIdentifier} from '../../../../../core/shared/basic/object-identifier';
import {DtoImportObjectContext} from '../../../../../core/shared/dto/dto.import.object.context';
import {PageHeaderConfig} from '../../../../../core/shared/service/page/page-header-config.interface';
import {
    MvsConfigHeaderComponent
} from '../../../../../core/shared/components/mvs-config-header/mvs-config-header.component';
import {
    WidgetFunctionCallBackCreate,
    WidgetToolbarCreateInterface
} from '../../../../../core/shared/helper/widget-function-call-back-create';
import {QlQueryDto} from '../../../../../core/shared/ql/data/dto/ql-query.dto';

@Component({
    selector: 'cs-config-page',
    templateUrl: './cs-config.page.html',
    styleUrls: ['./cs-config.page.scss'],
    standalone: false
})
export class CsConfigPage extends ConfigPageComponent implements OnInit {

    // CONFIG-011: child widget alias MUST be identical across all filter states
    // (all records / filtered by type / filtered by level). Extracted as constants
    // so build-level and build-option cannot accidentally diverge.
    private static readonly LEVEL_WIDGET_ALIAS = 'cs.config.referral.type.level.new';
    private static readonly OPTION_WIDGET_ALIAS = 'cs.config.referral.type.level.option.new';

    defaultLabel: string = 'Kunden Affiliate Programm';

    navigationItems: NavigationItem[] = [
        {
            label: 'cs.ReferralType',
            action: 'cs.ReferralType',
            tooltip: 'Empfehlungstypen',
            icon: 'fa-solid fa-diagram-project',
            toggleable: false,
            default: true
        },
        {
            label: 'cs.ReferralAccess',
            action: 'cs.ReferralAccess',
            tooltip: 'Zugriffsberechtigungen',
            icon: 'fa-solid fa-shield-halved',
            toggleable: false
        }
    ];

    override widgets: { [key: string]: WidgetConfigInterface } = {
        referralTypeSelectableWidget: {
            alias: 'cs.config.referral.type.selectable.new',
            label: 'Empfehlungstypen',
            entity: 'cs.ReferralType',
            widgetData: new WidgetData(),
            postFunction: (widget: WidgetConfigInterface) => {
                widget.widgetData.uiComponent = 'selectable';
                widget.widgetData.setParam(WidgetDataParam.create('showSearch', true));
                widget.widgetData.setParam(WidgetDataParam.create('attributeToDisplay', 'e.name'));
            }
        },
        referralAccessWidget: {
            alias: 'cs.config.referral.access.widget.simple.new',
            label: 'Zugriffsberechtigungen',
            entity: 'cs.ReferralAccess',
            widgetData: new WidgetData()
        }
    };

    // Runtime widgets (dynamic, created on selection)
    widgetReferralTypeLevel: WidgetData;
    widgetReferralTypeLevelOption: WidgetData;

    // Selection state
    selectedReferralType: ObjectIdentifierData;
    selectedReferralTypeLevel: ObjectIdentifierData;

    // Import contexts for child FK defaulting
    importContextReferralType: DtoImportObjectContext;
    importContextReferralTypeLevel: DtoImportObjectContext;

    constructor(
        protected router: Router,
        protected route: ActivatedRoute,
        protected coreService: MvsCoreService
    ) {
        super(route, coreService);
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.initialized = true;
    }

    override isNavComponent(): NavigationItem[] {
        return this.navigationItems;
    }

    override isConfigurationPage(): boolean {
        return true;
    }

    override postNavigationSelection() {
        if (this.activeNavigationItem?.action === 'cs.ReferralType') {
            const typeIdRaw = this.route.snapshot.queryParams['typeId'];
            const levelIdRaw = this.route.snapshot.queryParams['levelId'];
            const typeId = typeIdRaw ? +typeIdRaw : null;
            const levelId = levelIdRaw ? +levelIdRaw : null;

            // Widgets always render — filter state comes from URL params.
            // typeId null → Stufen show all, Optionen show all.
            // typeId set, levelId null → Stufen filtered by typeId, Optionen filtered by any of the type's levels (IN).
            // typeId set, levelId set → Stufen filtered by typeId, Optionen filtered by levelId.
            this.widgetReferralTypeLevel = this.buildLevelWidget(typeId);
            this.refreshOptionWidget(typeId, levelId);

            // Visual selection of the selectable widget cannot be restored from URL alone
            // without row data; leave the in-memory selection refs null on nav entry. The
            // filter state of the children is still correct; user re-selects for the sidebar
            // highlight if desired.
            this.selectedReferralType = null;
            this.selectedReferralTypeLevel = null;
            this.importContextReferralType = null;
            this.importContextReferralTypeLevel = null;
        }

        if (this.activeNavigationItem?.action === 'cs.ReferralAccess') {
            this.widgetReferralTypeLevel = null;
            this.widgetReferralTypeLevelOption = null;
        }

        // CONFIG-020 rule 3: clear stale state when leaving the ReferralType nav item.
        // typeId/levelId URL params are only meaningful inside ReferralType; on exit they
        // must be stripped so re-entry starts from a clean "show all" state. The selectable
        // ReferralType widget cannot visually restore its row highlight from a URL param, so
        // leaving the params intact causes a half-filtered child view with no visible parent
        // selection (see CONFIG-020 rationale).
        if (this.activeNavigationItem?.action !== 'cs.ReferralType') {
            this.selectedReferralType = null;
            this.selectedReferralTypeLevel = null;
            this.importContextReferralType = null;
            this.importContextReferralTypeLevel = null;

            const clearedParams: any = {};
            if (this.route.snapshot.queryParams['typeId']) {
                clearedParams.typeId = null;
            }
            if (this.route.snapshot.queryParams['levelId']) {
                clearedParams.levelId = null;
            }
            if (Object.keys(clearedParams).length > 0) {
                this.router.navigate([], {
                    queryParams: clearedParams,
                    queryParamsHandling: 'merge',
                    replaceUrl: true,
                });
            }
        }
    }

    // --- Selection handlers ---

    onReferralTypeSelect(event: ObjectIdentifierData | ObjectIdentifier) {
        const e = event as ObjectIdentifierData;
        this.selectedReferralType = e;
        const typeId = e.data['e.id'];

        this.importContextReferralType = DtoImportObjectContext.createFromObjectIdentifiers(event);
        this.selectedReferralTypeLevel = null;
        this.importContextReferralTypeLevel = null;

        this.widgetReferralTypeLevel = this.buildLevelWidget(typeId);
        this.refreshOptionWidget(typeId, null);

        this.router.navigate([], {
            queryParams: {typeId: typeId, levelId: null},
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });
    }

    onReferralTypeDeselect() {
        this.selectedReferralType = null;
        this.selectedReferralTypeLevel = null;
        this.importContextReferralType = null;
        this.importContextReferralTypeLevel = null;

        // Type cleared → both children revert to "show all".
        this.widgetReferralTypeLevel = this.buildLevelWidget(null);
        this.refreshOptionWidget(null, null);

        this.router.navigate([], {
            queryParams: {typeId: null, levelId: null},
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });
    }

    onReferralTypeLevelSelect(event: ObjectIdentifierData | ObjectIdentifier) {
        const e = event as ObjectIdentifierData;
        this.selectedReferralTypeLevel = e;
        const levelId = e.data['e.id'];

        this.importContextReferralTypeLevel = DtoImportObjectContext.createFromObjectIdentifiers(event);

        const typeId = this.selectedReferralType?.data['e.id'] ?? null;
        this.refreshOptionWidget(typeId, levelId);

        this.router.navigate([], {
            queryParams: {levelId: levelId},
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });
    }

    onReferralTypeLevelDeselect() {
        this.selectedReferralTypeLevel = null;
        this.importContextReferralTypeLevel = null;

        // Level cleared → fall back to type-level filter (or show all if no type selected).
        const typeId = this.selectedReferralType?.data['e.id'] ?? null;
        this.refreshOptionWidget(typeId, null);

        this.router.navigate([], {
            queryParams: {levelId: null},
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });
    }

    // --- Widget builders ---

    private buildLevelWidget(typeId: number | null): WidgetData {
        const filters = typeId != null
            ? [FilterCriteria.create('e.referralTypeId', FilterCriteria.cOperatorEqual, typeId)]
            : [];
        const label = typeId != null ? 'Stufen' : 'Alle Stufen';
        const widget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            CsConfigPage.LEVEL_WIDGET_ALIAS,
            label,
            'cs.ReferralTypeLevel',
            filters,
            [new Sorting('e.id', false)]
        );
        if (typeId != null) {
            const fields: WidgetToolbarCreateInterface[] = [
                {fieldName: 'referralTypeDtoId', fieldValue: typeId}
            ];
            widget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(fields);
        }
        return widget;
    }

    /**
     * Assigns the Optionen widget based on current Type/Level selection.
     *
     * Options has no direct FK to Type, so the Type-only case uses a QL query
     * with an explicit join alias on `referralTypeLevel` (as `l`) and filters
     * by `l.referralTypeId`. This mirrors the bm bankTransaction 3-column
     * cascade's SubFamily-filtered-by-Domain pattern: the default QL widget
     * factory does not introduce join aliases, so a plain filter like
     * `e.referralTypeLevel.referralTypeId` is rejected by the QL plan
     * generator ("Fieldnames within the filter need to have the content
     * reference (as) and the fieldname").
     */
    private refreshOptionWidget(typeId: number | null, levelId: number | null): void {
        if (levelId != null) {
            this.widgetReferralTypeLevelOption = this.buildOptionWidgetForLevel(levelId);
        } else if (typeId != null) {
            this.widgetReferralTypeLevelOption = this.buildOptionWidgetForType(typeId);
        } else {
            this.widgetReferralTypeLevelOption = this.buildOptionWidgetAll();
        }
    }

    private buildOptionWidgetForLevel(levelId: number): WidgetData {
        const widget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            CsConfigPage.OPTION_WIDGET_ALIAS,
            'Optionen',
            'cs.ReferralTypeLevelOption',
            [FilterCriteria.create('e.referralTypeLevelId', FilterCriteria.cOperatorEqual, levelId)],
            [new Sorting('e.id', false)]
        );
        const fields: WidgetToolbarCreateInterface[] = [
            {fieldName: 'referralTypeLevelDtoId', fieldValue: levelId}
        ];
        widget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(fields);
        return widget;
    }

    private buildOptionWidgetForType(typeId: number): WidgetData {
        // Join Options → Level (as `l`), then filter by l.referralTypeId.
        // resolve_fkey_texts=true keeps column rendering consistent with the
        // level-filtered state built by createWidgetTableEntityFkeyResolveQl.
        const qlQueryDto: QlQueryDto = {
            name: CsConfigPage.OPTION_WIDGET_ALIAS,
            type: 'view',
            filters: [
                FilterCriteria.create('l.referralTypeId', FilterCriteria.cOperatorEqual, typeId)
            ],
            start: {
                name: 'cs.ReferralTypeLevelOption',
                as: 'e',
                type: 'entity',
                joins: [
                    {
                        joinType: 'left',
                        type: 'entity',
                        name: 'referralTypeLevel',
                        as: 'l',
                    }
                ],
                parameters: [
                    {param: 'resolve_fkey_texts', value: 'true'}
                ]
            },
            sortings: [new Sorting('e.id', false)]
        };
        return WidgetFactory.createWidgetTableQl(
            CsConfigPage.OPTION_WIDGET_ALIAS,
            'Optionen',
            qlQueryDto,
        );
    }

    private buildOptionWidgetAll(): WidgetData {
        return WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            CsConfigPage.OPTION_WIDGET_ALIAS,
            'Alle Optionen',
            'cs.ReferralTypeLevelOption',
            [],
            [new Sorting('e.id', false)]
        );
    }

    getPageHeaderConfig(): PageHeaderConfig {
        return {
            component: MvsConfigHeaderComponent,
            inputs: {
                header: 'Kunden Affiliate Programm',
                categoryTypeMode: 'technical',
                icon: 'fa-solid fa-puzzle-piece'
            }
        };
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }
}
